import { useCallback, useState } from 'react';
import type {
  SerialisedError,
  SerialisedErrorCategory,
  SerialisedProcessOutput,
} from '@volley/crucible-ipc-types/error-types';
import { canInvokeChannel } from '@volley/crucible-client';
import { buildBugReportUrl } from '@volley/crucible-ipc-types/bug-report-url';
import { redactFlaggedSecrets } from '@volley/crucible-ipc-types/diagnostics-secret-scan';
import { useOptionalClientCapabilities } from '../transport/context';

/**
 * Board-grade error card for any IPC failure. Wired by the parent with
 * a deserialised error (see `unflattenIpcError`) plus optional retry +
 * navigation callbacks.
 *
 * Visual: red/10 background, red/40 border, icon, structured copy. Mirrors
 * the encryption-unavailable banner pattern called out in PR #112 — same
 * palette so error UX stays consistent across the app.
 *
 * The component must not crash when optional fields (`code`, `recovery`)
 * are absent; that's how non-CrucibleError throws arrive after
 * `unflattenIpcError`.
 */
export interface CrucibleErrorStateProps {
  error: SerialisedError;
  /**
   * If provided, a "Try again" button is rendered (regardless of
   * `error.retryable`) — the parent decides when retry makes sense in
   * its UI. If absent, no button appears because we have nothing to
   * wire it to.
   */
  onRetry?: () => void;
  /** Compact variant for inline rendering (e.g. inside a chat bubble). */
  compact?: boolean;
  /** Additional class names applied to the outer card. */
  className?: string;
}

/**
 * Copy text to the clipboard with a fallback for contexts the async
 * Clipboard API refuses.
 *
 * `navigator.clipboard.writeText` rejects in a cross-origin iframe with
 * no `clipboard-write` permission grant, on an unfocused document, and
 * on insecure origins — exactly the conditions the web / cloud GUI runs
 * under (issue #2179, where "Copy details for support" was a silent
 * no-op). Fall back to a hidden-textarea + `document.execCommand('copy')`,
 * which still works inside many of those iframe contexts. Returns whether
 * either path succeeded.
 */
async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Clipboard API refused — fall through to the execCommand path.
  }
  return copyViaExecCommand(text);
}

function copyViaExecCommand(text: string): boolean {
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Off-screen + read-only so it neither shifts layout nor pops the
  // mobile keyboard while we select-and-copy.
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  try {
    textarea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

export function CrucibleErrorState({
  error,
  onRetry,
  compact = false,
  className = '',
}: CrucibleErrorStateProps): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [reportOpening, setReportOpening] = useState(false);
  const [reportFailed, setReportFailed] = useState(false);

  // Issue #2179: the `app:open-bug-report` IPC is an Electron-main-only
  // handler. On the browser / cloud client it isn't invokable, so we
  // build the GitHub URL here and open it ourselves instead of firing a
  // doomed IPC call. Default to the native path when capabilities are
  // unknown (no provider — unit render / early boot).
  const capabilities = useOptionalClientCapabilities();
  const nativeBugReport = capabilities
    ? canInvokeChannel(capabilities, 'app:open-bug-report')
    : true;

  const buildSafePayload = useCallback((): string => {
    // JSON-stringify the structured payload, after redacting any
    // token-shaped strings and noisy absolute paths from the message
    // and the entire `cause` chain. Codex flagged this on PR #117 as a
    // real data-leak: the raw envelope can include the token value the
    // failing request used (e.g. an Anthropic 401 echoes back what was
    // sent). Sanitise before clipboard / bug-report, not after.
    //
    // Second-pass scrub (#2179 follow-up): `sanitiseForClipboard` uses
    // the legacy `redactSecrets` pattern set, which misses Datadog / AWS
    // secret-access-key / the high-entropy fallback. The bug-report path
    // gets those scrubbed downstream (`buildBugReportUrl` /
    // `app:open-bug-report`), but "Copy details for support" feeds this
    // payload straight to the clipboard — so run the same
    // `redactFlaggedSecrets` here to close the asymmetry at the single
    // chokepoint every consumer shares. Idempotent (marker-aware), so the
    // downstream builders re-running it is a no-op.
    return redactFlaggedSecrets(JSON.stringify(sanitiseForClipboard(error), null, 2)).text;
  }, [error]);

  const handleCopy = useCallback(() => {
    void copyTextToClipboard(buildSafePayload()).then((ok) => {
      if (ok) {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } else {
        // Both the async Clipboard API and the execCommand fallback
        // refused (issue #2179). Show a transient "copy failed" hint
        // rather than swallowing silently.
        setCopyFailed(true);
        window.setTimeout(() => setCopyFailed(false), 2500);
      }
    });
  }, [buildSafePayload]);

  const handleCreateBugReport = useCallback(() => {
    setReportOpening(true);
    setReportFailed(false);
    const errorJson = buildSafePayload();
    // Title is short because GitHub renders it prominently.
    const shortMessage = (error.message ?? 'Foundry error').slice(0, 70);
    const issueTitle = error.code ? `Bug: ${error.code} — ${shortMessage}` : `Bug: ${shortMessage}`;

    const finishSoon = (): void => {
      // Slight delay so the "Opening…" affordance is visible even on
      // fast machines — otherwise the click feels like nothing happened
      // until the browser pops.
      window.setTimeout(() => setReportOpening(false), 900);
    };
    const markFailed = (): void => {
      setReportFailed(true);
      window.setTimeout(() => setReportFailed(false), 3000);
    };

    if (nativeBugReport) {
      // Electron desktop: hand the sanitised payload to main, which
      // builds the pre-filled GitHub URL, opens the user's browser, and
      // mirrors the full payload to the clipboard as a backstop.
      void window.crucible
        .invoke('app:open-bug-report', { errorJson, title: issueTitle })
        .catch(() => {
          // The desktop IPC failed unexpectedly — copy the payload so
          // the user still has something to paste, and surface it.
          void copyTextToClipboard(errorJson);
          markFailed();
        })
        .finally(finishSoon);
      return;
    }

    // Web / cloud: there is no Electron main to service the IPC, so
    // build the pre-filled GitHub issue URL here and open it ourselves
    // (issue #2179). `window.open` runs synchronously inside the click
    // so it keeps the transient user-activation a popup blocker needs.
    // The full body always lands on the clipboard as the backstop for
    // when the URL was truncated to fit GitHub's length cap.
    try {
      const { url, body } = buildBugReportUrl({ errorJson, title: issueTitle });
      void copyTextToClipboard(body);
      const win = window.open(url, '_blank');
      if (win) {
        // Sever the opener reference (the security win of `noopener`)
        // while still being able to detect a blocked popup via the
        // return value — `window.open(url, '_blank', 'noopener')` returns
        // null even on success, so it can't be used for that check.
        win.opener = null;
      } else {
        markFailed();
      }
    } catch {
      markFailed();
    } finally {
      finishSoon();
    }
  }, [buildSafePayload, error.code, error.message, nativeBugReport]);

  const title = titleForCategory(error.category);
  const body = redactPathNoise(error.message);
  const showRetry = !!onRetry;

  return (
    <div
      role="alert"
      data-testid="crucible-error-state"
      className={[
        'rounded-lg border border-red-500/40 bg-red-500/10',
        compact ? 'p-3' : 'p-4',
        'space-y-2',
        // Issue #328: error body text must be selectable so users can
        // copy the message into a bug report or Slack without leaning on
        // the "Copy details" button (board members reach for OS-level
        // select+copy first). `select-text` is scoped to the card; the
        // action buttons below remain non-selectable by virtue of being
        // `<button>` elements. `cursor-text` advertises the affordance,
        // kept consistent with the chat-bubble shim and tool-result
        // `<pre>` (Codex flagged the inconsistency on PR #341).
        'cursor-text select-text',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-start gap-2">
        <svg
          width={compact ? 16 : 18}
          height={compact ? 16 : 18}
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-red-400"
        >
          <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10 6v4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="10" cy="13.25" r="0.85" fill="currentColor" />
        </svg>
        <div className="flex-1">
          <h3 className={compact ? 'text-sm font-medium text-red-200' : 'font-medium text-red-200'}>
            {title}
          </h3>
          {body && (
            <p
              className={
                compact ? 'mt-0.5 text-xs text-red-100/80' : 'mt-1 text-sm text-red-100/80'
              }
            >
              {body}
            </p>
          )}
          {error.recovery && (
            <p
              className={compact ? 'mt-1 text-xs text-red-100/60' : 'mt-2 text-sm text-red-100/70'}
            >
              {error.recovery}
            </p>
          )}
          {error.code && (
            <p className="mt-1 font-mono text-[10px] uppercase tracking-wide text-red-200/40">
              {error.code}
            </p>
          )}
          {/* Issue #266 / CRUCIBLE-405: surface the orchestrator-side
              process diagnostic when present. Collapsed by default so
              the card stays compact for the typical "read the recovery
              line and act" flow; expandable for the user that wants the
              tail + argv + cwd. */}
          {error.processOutput && (
            <ProcessOutputBlock processOutput={error.processOutput} compact={compact} />
          )}
        </div>
      </div>
      {/* Action row — Retry (if wired), Create Bug Report, Copy. The
          bug-report button is always available alongside Copy: it's the
          one-click escape hatch that opens a pre-filled GitHub issue
          AND drops the full sanitised payload on the clipboard, so the
          board-member path ("I hit a bug, please file it") doesn't
          require them to know what a JSON envelope is. */}
      <div className="flex flex-wrap items-center gap-2">
        {showRetry && (
          <button
            type="button"
            onClick={onRetry}
            // autoFocus places keyboard focus on the recovery action — a
            // board member who sees an error wants to act, not read; this
            // makes Enter/Space the natural next press. Codex flagged the
            // missing focus on PR #117.
            autoFocus
            data-testid="crucible-error-retry"
            className="rounded-md bg-red-500/20 px-3 py-1 text-xs font-medium text-red-100 transition-colors hover:bg-red-500/30 focus:outline-none focus:ring-2 focus:ring-red-300/60"
          >
            Try again
          </button>
        )}
        <button
          type="button"
          onClick={handleCreateBugReport}
          disabled={reportOpening}
          data-testid="crucible-error-bug-report"
          className="rounded-md border border-red-500/40 px-3 py-1 text-xs text-red-200/80 transition-colors hover:bg-red-500/10 focus:outline-none focus:ring-2 focus:ring-red-300/60 disabled:opacity-60"
        >
          {reportOpening
            ? 'Opening GitHub…'
            : reportFailed
              ? "Couldn't open — details copied"
              : 'Create bug report'}
        </button>
        <button
          type="button"
          onClick={handleCopy}
          data-testid="crucible-error-copy"
          className="rounded-md border border-red-500/40 px-3 py-1 text-xs text-red-200/80 transition-colors hover:bg-red-500/10 focus:outline-none focus:ring-2 focus:ring-red-300/60"
        >
          {copyFailed ? 'Copy failed — try again' : copied ? 'Copied' : 'Copy details for support'}
        </button>
      </div>
    </div>
  );
}

/**
 * Map a category to a board-friendly title. The body (`error.message`)
 * carries the specifics; the title gives the user a single phrase they
 * can quote in a Slack message.
 *
 * Exported for unit tests; not part of the public API.
 */
export function titleForCategory(category: SerialisedErrorCategory): string {
  switch (category) {
    case 'auth':
      return 'Sign-in problem';
    case 'network':
      return 'Connection problem';
    case 'git':
      return 'GitHub problem';
    case 'template':
      return 'Template problem';
    case 'usage':
      return 'Something to fix in the request';
    case 'filesystem':
      return 'File or folder problem';
    case 'state':
      return 'State problem';
    // Issue #484 — `agentError` factory in `@volley/crucible/util/errors`
    // tags Foundry-emitted failures (CRUCIBLE-FOUNDRY-* codes from
    // `agent-adapter.ts`, `agent-errors.ts`, `install-state.ts`) with
    // `category: 'agent'`. Round-tripped through the envelope so the user
    // sees a Foundry-specific title rather than the generic fallback.
    case 'agent':
      return 'Foundry problem';
    case 'unknown':
    default:
      return 'Something went wrong';
  }
}

/**
 * Smart-redact long absolute paths from a message so the dialog isn't
 * dominated by `/Users/foo/Library/Application Support/Foundry/...`.
 *
 * Heuristic: collapse any sequence of >= 3 path segments into the
 * basename. Conservative — leaves short relative paths and URLs alone.
 *
 * Exported for unit tests; not part of the public API.
 */
export function redactPathNoise(message: string): string {
  if (!message) return message;
  // Replace POSIX-style absolute paths: /a/b/c/d/file.ext -> .../file.ext
  let out = message.replace(/(\/[\w.\- ]+){3,}/g, (match) => {
    const parts = match.split('/').filter(Boolean);
    return '.../' + parts[parts.length - 1];
  });
  // Replace Windows-style absolute paths: C:\a\b\c\file.ext -> ...\file.ext
  out = out.replace(/[A-Za-z]:\\(?:[\w.\- ]+\\){2,}[\w.\- ]+/g, (match) => {
    const parts = match.split('\\');
    return '...\\' + parts[parts.length - 1];
  });
  return out;
}

/**
 * Token-shaped redaction sweep used by both the clipboard payload
 * here and the Settings → Diagnostics dump in `main/diagnostics.ts`.
 * Re-exported (rather than redefined) so the existing test in
 * `__tests__/CrucibleErrorState.test.tsx` keeps importing the
 * function from this module while the implementation lives in
 * `shared/redact-secrets.ts`.
 */
import { redactSecrets } from '@volley/crucible-ipc-types/redact-secrets';
export { redactSecrets };

/**
 * Recursively sanitise a `SerialisedError` for the "Copy details for
 * support" clipboard action. Strips token-shaped strings from every
 * string field (message, recovery, code) and from the entire `cause`
 * chain, depth-limited so a cycle can't blow the stack.
 *
 * Exported for unit tests; not part of the public API.
 */
export function sanitiseForClipboard(error: SerialisedError, depth = 0): SerialisedError {
  if (depth > 8) {
    // Hit the depth fence — return a marker rather than recurring further.
    return {
      name: error.name,
      message: '[REDACTED-DEPTH-LIMIT]',
      category: error.category,
      retryable: false,
    };
  }
  const safe: SerialisedError = {
    name: error.name,
    message: redactSecrets(redactPathNoise(error.message)),
    category: error.category,
    retryable: error.retryable,
  };
  if (error.code !== undefined) safe.code = redactSecrets(error.code);
  if (error.recovery !== undefined) safe.recovery = redactSecrets(error.recovery);
  if (error.systemError) {
    // Whitelist + redact each field. Codes (ENOTDIR, ENOENT) and
    // syscalls (spawn, open, stat) are short well-known strings and
    // need no scrubbing; `path` and `dest` may contain home-directory
    // paths so we run them through the same path-noise + secret
    // redaction the message gets.
    safe.systemError = {
      ...(error.systemError.code !== undefined ? { code: error.systemError.code } : {}),
      ...(error.systemError.errno !== undefined ? { errno: error.systemError.errno } : {}),
      ...(error.systemError.syscall !== undefined ? { syscall: error.systemError.syscall } : {}),
      ...(error.systemError.path !== undefined
        ? { path: redactSecrets(redactPathNoise(error.systemError.path)) }
        : {}),
      ...(error.systemError.dest !== undefined
        ? { dest: redactSecrets(redactPathNoise(error.systemError.dest)) }
        : {}),
    };
  }
  if (error.processOutput) {
    // Issue #266: scrub the captured stdout / stderr tails through the
    // same secret + path redaction the message gets. Most game logs
    // are clean but a stray `process.env.ANTHROPIC_API_KEY` echo in a
    // dev script would otherwise reach the clipboard verbatim. The
    // argv array is whitelisted (cmd-escaped pnpm flags only) so it
    // doesn't usually need redaction, but a `--launch-url` carrying a
    // tokened query string would — run it through the redactor too.
    safe.processOutput = {
      ...(error.processOutput.name !== undefined ? { name: error.processOutput.name } : {}),
      ...(error.processOutput.stdoutTail !== undefined
        ? {
            stdoutTail: error.processOutput.stdoutTail.map((line) =>
              redactSecrets(redactPathNoise(line)),
            ),
          }
        : {}),
      ...(error.processOutput.stderrTail !== undefined
        ? {
            stderrTail: error.processOutput.stderrTail.map((line) =>
              redactSecrets(redactPathNoise(line)),
            ),
          }
        : {}),
      ...(error.processOutput.exitCode !== undefined
        ? { exitCode: error.processOutput.exitCode }
        : {}),
      ...(error.processOutput.signal !== undefined ? { signal: error.processOutput.signal } : {}),
      ...(error.processOutput.durationMs !== undefined
        ? { durationMs: error.processOutput.durationMs }
        : {}),
      ...(error.processOutput.binary !== undefined ? { binary: error.processOutput.binary } : {}),
      ...(error.processOutput.argv !== undefined
        ? {
            argv: error.processOutput.argv.map((arg) => redactSecrets(redactPathNoise(arg))),
          }
        : {}),
      ...(error.processOutput.cwd !== undefined
        ? { cwd: redactSecrets(redactPathNoise(error.processOutput.cwd)) }
        : {}),
    };
  }
  if (error.cause) safe.cause = sanitiseForClipboard(error.cause, depth + 1);
  return safe;
}

/**
 * Issue #266 / CRUCIBLE-405: render the orchestrator-side process
 * diagnostic. Collapsed by default — the user reads the recovery line
 * first and only opens the diagnostic if they want the tail / argv /
 * cwd. The block is rendered as a `<details>` so keyboard users can
 * tab into it and Space-toggle it without bespoke handlers.
 *
 * Surfaces (in this order):
 *   1. Process name + duration + exit + signal — one-line summary
 *   2. cwd — usually the most user-visible signal ("oh, wrong folder")
 *   3. binary + argv — the EXACT command that ran
 *   4. stderr tail — usually the load-bearing signal when populated
 *   5. stdout tail — same, but separate so a chatty stdout doesn't
 *      drown a one-line stderr
 *
 * When stdout AND stderr are both empty the block prints
 * "(no output captured)" rather than rendering empty blocks — that's
 * the load-bearing diagnostic on its own, and matches the language in
 * the recovery copy the orchestrator picked.
 */
export function ProcessOutputBlock({
  processOutput,
  compact,
}: {
  processOutput: SerialisedProcessOutput;
  compact?: boolean;
}): React.JSX.Element {
  const stdoutEmpty = !processOutput.stdoutTail || processOutput.stdoutTail.length === 0;
  const stderrEmpty = !processOutput.stderrTail || processOutput.stderrTail.length === 0;
  const summaryParts: string[] = [];
  if (processOutput.name) summaryParts.push(`process: ${processOutput.name}`);
  if (processOutput.exitCode !== undefined && processOutput.exitCode !== null) {
    summaryParts.push(`exit ${processOutput.exitCode}`);
  } else if (processOutput.signal) {
    summaryParts.push(`signal ${processOutput.signal}`);
  }
  if (processOutput.durationMs !== undefined) {
    summaryParts.push(`${processOutput.durationMs}ms`);
  }
  const summary = summaryParts.join(' · ') || 'process diagnostic';

  return (
    <details
      data-testid="crucible-error-process-output"
      className={compact ? 'mt-1 text-xs' : 'mt-2 text-xs'}
    >
      <summary className="cursor-pointer text-red-200/70 hover:text-red-100">
        Diagnostic details — {summary}
      </summary>
      <div className="mt-2 space-y-2 rounded border border-red-500/20 bg-black/20 p-2 font-mono text-[11px] text-red-100/80">
        {processOutput.cwd && (
          <div>
            <span className="text-red-200/50">cwd:</span> {redactPathNoise(processOutput.cwd)}
          </div>
        )}
        {(processOutput.binary || processOutput.argv) && (
          <div>
            <span className="text-red-200/50">argv:</span> {processOutput.binary ?? ''}
            {processOutput.argv && processOutput.argv.length > 0
              ? ' ' + processOutput.argv.join(' ')
              : ''}
          </div>
        )}
        {stdoutEmpty && stderrEmpty ? (
          <div className="italic text-red-100/50">(no output captured)</div>
        ) : (
          <>
            {!stderrEmpty && (
              <div>
                <div className="text-red-200/50">stderr (tail):</div>
                <pre className="overflow-x-auto whitespace-pre-wrap text-red-100/90">
                  {processOutput.stderrTail!.map((line) => redactPathNoise(line)).join('\n')}
                </pre>
              </div>
            )}
            {!stdoutEmpty && (
              <div>
                <div className="text-red-200/50">stdout (tail):</div>
                <pre className="overflow-x-auto whitespace-pre-wrap text-red-100/70">
                  {processOutput.stdoutTail!.map((line) => redactPathNoise(line)).join('\n')}
                </pre>
              </div>
            )}
          </>
        )}
      </div>
    </details>
  );
}
